import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, CalendarEvent } from '../../../../components/Calendar';
import { useAppPreCallPlans } from '../../../../hooks/useAppPreCallPlan';
import { Heading } from '../../../../components/Heading';
import { Button } from '../../../../components/Button';
import { Badge } from '../../../../components/Badge';
import Loading from '../../../../components/Loading';
import { UserContext } from '../../../../context/userContext';
import dateFormat from 'dateformat';

const PreCallPlanCalendarPage: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);
    const { loading, error, fetchPreCallPlansPaginated, preCallPlans } = useAppPreCallPlans();
    const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

    useEffect(() => {
        document.title = "Pre Call Plans - Calendar View";
        // Fetch all pre-call plans for the calendar (we'll need to implement this)
        fetchPreCallPlansPaginated(1, 1000); // Fetch all plans for now
    }, [fetchPreCallPlansPaginated]);

    useEffect(() => {
        if (preCallPlans.length > 0) {
            // Convert pre-call plans to calendar events
            const events: CalendarEvent[] = preCallPlans.map(plan => ({
                id: plan.id,
                title: `Pre-Call: ${plan.customer_fullname || 'Unknown Customer'}`,
                date: plan.planned_date || plan.created_time, // Use planned_date as primary, fallback to created_time
                type: 'precall' as const,
                customer: plan.customer_fullname || 'Unknown Customer',
                salesman: plan.salesman_username || 'Unknown Salesman',
                status: plan.post_call_plan_exist === '1' ? 'Completed' : 'Pending'
            }));
            setCalendarEvents(events);
        }
    }, [preCallPlans]);

    const handleEventClick = (event: CalendarEvent) => {
        setSelectedEvent(event);
        // Navigate to the pre-call plan view
        navigate(`/gp/app/precallplans/view/${event.id}`);
    };

    const handleDateClick = (date: Date) => {
        setSelectedDate(date);
        // You could implement date-specific actions here
        console.log('Selected date:', date);
    };

    const getPlansForSelectedDate = () => {
        if (!selectedDate) return [];
        const dateString = selectedDate.toDateString();
        return calendarEvents.filter(event =>
            new Date(event.date).toDateString() === dateString
        );
    };

    const getUpcomingPlans = () => {
        const today = new Date();
        return calendarEvents
            .filter(event => new Date(event.date) >= today)
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
            .slice(0, 5);
    };

    const getPendingPlans = () => {
        return calendarEvents.filter(event => event.status === 'Pending');
    };

    if (loading) return <Loading />;
    if (error) return <p className="text-red-600 text-center py-8">{error}</p>;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-4">
                <Heading>Pre Call Plans - Calendar View</Heading>
                <div className="flex items-center space-x-3">
                    <Button onClick={() => navigate('/gp/app/precallplans')} outline>
                        List View
                    </Button>

                </div>
            </div>

            {/* Calendar */}
            <Calendar
                events={calendarEvents}
                onEventClick={handleEventClick}
                onDateClick={handleDateClick}
            />

            {/* Additional Information Tabs */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Selected Date Details */}
                {selectedDate && (
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            Plans for {dateFormat(selectedDate, "mmmm dd, yyyy")}
                        </h3>
                        {getPlansForSelectedDate().length > 0 ? (
                            <div className="space-y-3">
                                {getPlansForSelectedDate().map(event => (
                                    <div
                                        key={event.id}
                                        className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                                        onClick={() => handleEventClick(event)}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="font-medium text-gray-900">{event.customer}</div>
                                                <div className="text-sm text-gray-500">{event.salesman}</div>
                                            </div>
                                            <Badge color={event.status === 'Completed' ? 'green' : 'yellow'}>
                                                {event.status}
                                            </Badge>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 text-center py-4">No plans scheduled for this date</p>
                        )}
                    </div>
                )}

                {/* Quick Stats */}
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Overview</h3>
                    <div className="space-y-4">
                        <div>
                            <div className="text-sm font-medium text-gray-500">Upcoming Plans</div>
                            <div className="text-2xl font-bold text-blue-600">{getUpcomingPlans().length}</div>
                        </div>
                        <div>
                            <div className="text-sm font-medium text-gray-500">Pending Plans</div>
                            <div className="text-2xl font-bold text-yellow-600">{getPendingPlans().length}</div>
                        </div>
                        <div>
                            <div className="text-sm font-medium text-gray-500">Total Plans</div>
                            <div className="text-2xl font-bold text-gray-900">{calendarEvents.length}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Upcoming Plans List */}
            <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Upcoming Plans</h3>
                </div>
                <div className="p-6">
                    {getUpcomingPlans().length > 0 ? (
                        <div className="space-y-3">
                            {getUpcomingPlans().map(event => (
                                <div
                                    key={event.id}
                                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                                    onClick={() => handleEventClick(event)}
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                        <div>
                                            <div className="font-medium text-gray-900">{event.customer}</div>
                                            <div className="text-sm text-gray-500">
                                                {event.salesman} • {dateFormat(event.date, "mmm dd, yyyy")}
                                            </div>
                                        </div>
                                    </div>
                                    <Badge color={event.status === 'Completed' ? 'green' : 'yellow'}>
                                        {event.status}
                                    </Badge>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500 text-center py-4">No upcoming plans</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PreCallPlanCalendarPage;
